import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Clock, Mail, Phone, CheckCircle } from 'lucide-react';

export default function AdminFMCSARequestsNew() {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('fmcsa_change_requested', true)
      .order('fmcsa_change_requested_at', { ascending: false });

    if (error) {
      console.error('Error loading requests:', error);
      toast.error('Failed to load requests');
      return;
    }

    setRequests(data || []);
    setLoading(false);
  };

  const handleResolve = async (profileId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to mark this request as resolved? Make sure you have manually updated the FMCSA data if needed.'
    );

    if (!confirmed) return;

    setProcessingId(profileId);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          fmcsa_change_requested: false,
          fmcsa_change_request_reason: null,
          fmcsa_change_requested_at: null,
        })
        .eq('id', profileId);

      if (error) throw error;

      toast.success('Request marked as resolved');
      loadRequests();
    } catch (error) {
      console.error('Error processing request:', error);
      toast.error('Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">FMCSA Change Requests</h1>
          <p className="text-[#6b7280] mt-1">
            Review and process user requests to change their FMCSA authority information
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#14b8a6]"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id} className="bg-[#111820] border-[#1e2530] p-6">
                <div className="flex justify-between items-start gap-6">
                  <div className="flex-1 space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg text-white mb-1">
                        {request.fmcsa_legal_name || 'No Legal Name'}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-[#6b7280]">
                        <span>MC: {request.fmcsa_mc_number || 'N/A'}</span>
                        <span>DOT: {request.fmcsa_dot_number || 'N/A'}</span>
                        <span>Type: {request.fmcsa_authority_type || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-[#9ca3af]">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#6b7280]" />
                        <span>{request.email}</span>
                      </div>
                      {request.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-[#6b7280]" />
                          <span>{request.phone}</span>
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-[#0d1117] rounded-lg border border-[#1e2530]">
                      <p className="text-sm font-medium text-[#9ca3af] mb-2">Reason for Change Request:</p>
                      <p className="text-sm text-white">{request.fmcsa_change_request_reason}</p>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-[#6b7280]">
                      <Clock className="w-3 h-3" />
                      <span>
                        Requested: {new Date(request.fmcsa_change_requested_at).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleResolve(request.id)}
                      disabled={processingId === request.id}
                      size="sm"
                      className="bg-[#14b8a6] hover:bg-[#0d9488] text-white"
                    >
                      {processingId === request.id ? 'Processing...' : 'Mark as Resolved'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#1e2530] text-[#9ca3af] hover:text-white"
                      onClick={() => {
                        navigator.clipboard.writeText(request.email);
                        toast.success('Email copied to clipboard');
                      }}
                    >
                      Copy Email
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {requests.length === 0 && (
              <Card className="bg-[#111820] border-[#1e2530] p-12">
                <div className="text-center text-[#6b7280]">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50 text-emerald-400" />
                  <p className="text-lg font-medium text-white">No pending change requests</p>
                  <p className="text-sm mt-2">
                    All FMCSA change requests have been processed
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
